import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../shared/Icon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ReportSteps.css';
import { apiService } from '../../../../services/api';
import { parseDatasetPrefix, resolveDatasetRefs } from '../resolveDatasetRefs';
import { isDatasetActive } from '../../../../utils/datasetReviewState';
import ReviewRequiredBanner from '../../shared/ReviewRequiredBanner';
import DatasetMultiSelect from '../../shared/DatasetMultiSelect';

/**
 * The Query step, shared by the Reports and Alerts wizards.
 *
 * `title`, `nameLabel` and `namePlaceholder` exist so the Alerts wizard can
 * reuse this step rather than clone it — PRD-1248 specifies "keep the Report
 * query step exactly", and a copy would drift. The defaults are the Reports
 * wording, so the Reports flow is unchanged.
 *
 * `runAction` and `preview` are the two places where "exactly" stops being
 * right, and they are props for the same reason the wording is: so the Alerts
 * wizard can differ in the two spots where it must, and nowhere else.
 *
 *  - **`runAction`** replaces REGENERATE. REGENERATE runs the chat query, which
 *    on an alert is not the query the alert runs — leaving it in place gave the
 *    author a button that previews a different definition of their alert from
 *    the one that gets saved, which is precisely the split PRD-1313 closes.
 *  - **`preview`** replaces the markdown response panel, because what the alerts
 *    variant has to show is the authored query's rows, not a narrative answer.
 *
 * Both default to null, so the Reports flow keeps its own button and panel and
 * this file's behaviour there is unchanged. Raised in review of #177.
 *
 *  - **`showRunAndPreview`** drops both, for a variant with neither to offer.
 *    The alert conversation shows the rows its built alert matches inside the
 *    conversation itself, so the report's REGENERATE and "Response Preview"
 *    there were leftovers: a button that ran the chat query and a panel that
 *    read like the alert's message and said "the report will be regenerated".
 *  - **`showSavedQuery`** drops the "Select Saved Query" picker, for the same
 *    variant. Picking a past Ask ResAI question there filled the report's query
 *    text, which the conversation never reads, and replaced the selected
 *    datasets mid-conversation. Off, the step does not fetch the list either.
 *  - **`showDatasetRequiredNote`** drops the "A dataset is required" banner, for
 *    the same variant: it greeted the author before they had typed anything.
 */
const ReportStepQuery = ({
  formData,
  onSetFormData,
  title = 'Query',
  nameLabel = 'Report name',
  namePlaceholder = 'e.g. Early attrition analysis',
  queryLabel = 'Edit Query',
  queryPlaceholder = 'Enter or edit your report query',
  queryHelper = null,
  // When supplied, replaces the query textarea and its helper. The label stays,
  // so the field still reads as the same field. May be a function, called with
  // the datasets this step loaded, the selected ids, and the same toggle the
  // dataset picker uses — so a control that shows the selection reads one list
  // and changes one piece of state, rather than fetching every dataset again.
  queryControl = null,
  runAction = null,
  previewLabel = 'Response Preview',
  preview = null,
  showRunAndPreview = true,
  showSavedQuery = true,
  showDatasetRequiredNote = true,
}) => {
  const [threads, setThreads] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingDatasets, setLoadingDatasets] = useState(true);
  const [loadingState, setLoadingState] = useState(false);
  const [responsePreview, setResponsePreview] = useState('');
  // Dataset references a saved query carries that no longer resolve — deleted,
  // renamed, or no longer readable by this user. Held rather than dropped: the
  // previous behaviour was to load the query with fewer datasets than it was
  // saved with, or none, and say nothing about why.
  const [unresolvedDatasets, setUnresolvedDatasets] = useState([]);
  const datasetIdsRef = useRef([]);
  // Guards the full page walk below against overlapping itself on the poll.
  const datasetWalkInFlight = useRef(false);

  const queryText = formData.reportQuery ?? formData.queryName ?? '';
  const selectedThreadId = formData.selectedThreadId ?? '';
  const selectedDatasetIds = formData.reportDatasetIds ?? [];

  // Keep ref in sync so async handlers always read the latest
  datasetIdsRef.current = selectedDatasetIds;

  // PRD-1359: `silent` keeps the poll from flipping the dropdown back to
  // "Loading..." under the user every few seconds.
  const loadDatasets = useCallback(async ({ silent = false } = {}) => {
    // One walk at a time. This is a full page walk on a 5s poll, so in a large
    // org it can take longer than the interval — without this the next tick
    // starts before the last finished, they overlap, and the OLDER response can
    // resolve last and win. Skipping a tick is free: the one in flight is about
    // to deliver the same thing. Raised in code review on PR #180.
    if (datasetWalkInFlight.current) return;
    datasetWalkInFlight.current = true;
    if (!silent) setLoadingDatasets(true);
    try {
      // Every page, not just the first. This list IS the dropdown, so a capped
      // fetch did not merely weaken the review warning — it made datasets past
      // the first page unselectable, with nothing on screen to say so. No early
      // exit here: unlike the step 4 hook, there is no known set to stop at.
      setDatasets(await apiService.getAllDatasets());
    } catch (err) {
      console.error('Failed to load datasets:', err);
      // A failed poll must not blank a list the user is picking from.
      if (!silent) setDatasets([]);
    } finally {
      datasetWalkInFlight.current = false;
      if (!silent) setLoadingDatasets(false);
    }
  }, []);

  // PRD-1359 AC: "Counts in the copy reflect file_counts.awaiting_review and
  // update without a manual refresh." This step used to fetch once on mount, so
  // a reviewer clearing the queue in another tab left the chip and the banner
  // stale for as long as the wizard stayed open. Gated on isDatasetActive so a
  // wizard over settled datasets does no polling at all.
  useEffect(() => {
    if (!datasets.some((d) => isDatasetActive(d))) return undefined;
    const timer = setInterval(() => loadDatasets({ silent: true }), 5000);
    return () => clearInterval(timer);
  }, [datasets, loadDatasets]);

  // Load threads and datasets on mount
  useEffect(() => {
    const loadThreads = async () => {
      // No picker, no list: a page of past conversations nobody can choose from.
      if (!showSavedQuery) {
        setLoadingThreads(false);
        return;
      }
      setLoadingThreads(true);
      try {
        const response = await apiService.getThreads({ page: 1, page_size: 100 });
        setThreads(response?.items ?? []);
      } catch (err) {
        console.error('Failed to load saved queries:', err);
        setThreads([]);
      } finally {
        setLoadingThreads(false);
      }
    };
    loadThreads();
    loadDatasets();
  }, [loadDatasets, showSavedQuery]);

  /** Normalize message content to string. */
  const normalizeContentToString = (content) => {
    if (content == null) return '';
    if (typeof content === 'string') return content.trim();
    if (Array.isArray(content)) {
      return content
        .map((part) => (part?.text != null ? part.text : part?.content != null ? part.content : ''))
        .filter(Boolean)
        .join(' ')
        .trim();
    }
    return String(content).trim();
  };

  // Auto-select datasets from threadDatasetIds when available
  useEffect(() => {
    if (selectedDatasetIds.length === 0 && formData.threadDatasetIds?.length > 0) {
      onSetFormData((prev) => ({
        ...prev,
        reportDatasetIds: formData.threadDatasetIds.map(String),
      }));
    }
  }, [formData.threadDatasetIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit-mode heuristic: match the saved-query dropdown by query text vs
  // thread.title (truncated to 50 chars + "..." by the chat). The query text
  // is stable across post-create renames; automationName isn't. One-shot via
  // a ref so a manual clear/change isn't re-snapped back.
  const titleMatchedRef = useRef(false);
  useEffect(() => {
    if (titleMatchedRef.current) return;
    if (loadingThreads || loadingDatasets) return;
    if (selectedThreadId) {
      titleMatchedRef.current = true;
      return;
    }
    const queryNorm = (queryText || '').trim();
    if (!queryNorm || threads.length === 0) return;
    const stripEllipsis = (s) => (s || '').replace(/\.{3}$/u, '').replace(/…$/u, '').trim();
    const match = threads.find((t) => {
      const title = stripEllipsis(t.title || '');
      if (!title) return false;
      // Exact match (short queries) OR prefix match (long queries truncated
      // by the chat to a leading window). Case-insensitive — the chat doesn't
      // change casing but normalize for safety.
      return (
        title.toLowerCase() === queryNorm.toLowerCase()
        || queryNorm.toLowerCase().startsWith(title.toLowerCase())
      );
    });
    if (!match) return;
    titleMatchedRef.current = true;
    const matchId = String(match.id);
    // Set the dropdown immediately so the UI reflects the match even if the
    // dataset recovery below takes a beat (or fails).
    onSetFormData((prev) => ({
      ...prev,
      selectedThreadId: matchId,
      selectedSavedQueryTitle: match.title || '',
    }));
    // Only pull datasets from the thread when the row currently has none —
    // respect any post-create edits the user already saved.
    if (selectedDatasetIds.length > 0) return;
    (async () => {
      try {
        const state = await apiService.getThreadState(matchId);
        const messages = state?.values?.messages ?? [];
        let refs = [];
        for (const m of messages) {
          if (m.type !== 'human') continue;
          refs = parseDatasetPrefix(normalizeContentToString(m.content));
          if (refs.length) break;
        }
        // Resolved against the API rather than against whatever the picker
        // happened to load: `datasets` here is one page of 100, which is also
        // the server's maximum, so a name past the first hundred could never be
        // found and the reference was dropped in silence.
        const { resolved, unresolved } = await resolveDatasetRefs(refs, apiService);
        setUnresolvedDatasets(unresolved);
        if (resolved.length > 0) {
          onSetFormData((prev) => ({
            ...prev,
            reportDatasetIds: resolved,
          }));
        }
      } catch (err) {
        console.warn('Could not recover dataset from source thread:', err);
      }
    })();
    // `datasets` is no longer a dependency: this effect used to resolve names
    // against that loaded page and now asks the API instead. The
    // `loadingDatasets` guard stays — the picker below still needs it before the
    // step is usable.
  }, [loadingThreads, loadingDatasets, threads, queryText, selectedThreadId, selectedDatasetIds.length, onSetFormData]);

  const handleSelectSavedQuery = async (e) => {
    const threadId = e.target.value;
    if (!threadId) {
      onSetFormData({
        ...formData,
        selectedThreadId: '',
        selectedSavedQueryTitle: '',
        reportQuery: '',
        queryName: '',
        responsePreview: '',
        automationName: '',
      });
      setResponsePreview('');
      return;
    }
    const thread = threads.find((t) => t.id === threadId);
    const queryTitle = thread?.title || '';
    onSetFormData({
      ...formData,
      selectedThreadId: threadId,
      selectedSavedQueryTitle: queryTitle,
      automationName: queryTitle,
    });
    setLoadingState(true);
    try {
      const state = await apiService.getThreadState(threadId);
      const messages = state?.values?.messages ?? [];
      const firstHuman = messages.find((m) => m.type === 'human');
      const rawQuery = firstHuman ? normalizeContentToString(firstHuman.content) : '';
      // The [Datasets: ...] prefix the saved query carries. Shared with the
      // recovery path above rather than repeated — the two copies of this had
      // the same two defects and differed only in wording.
      let refs = [];
      for (const m of messages) {
        if (m.type !== 'human') continue;
        refs = parseDatasetPrefix(normalizeContentToString(m.content));
        if (refs.length) break;
      }
      const { resolved: resolvedDatasetIds, unresolved } = await resolveDatasetRefs(refs, apiService);
      setUnresolvedDatasets(unresolved);
      const queryFromThread = rawQuery.replace(/^\[Datasets?:.*\]\s*/i, '').replace(/^\[Files?: .+\]\s*/i, '').trim();
      let lastAiContent = '';
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg?.type === 'ai' && msg?.content != null) {
          lastAiContent = normalizeContentToString(msg.content);
          break;
        }
      }
      onSetFormData((prev) => ({
        ...prev,
        reportQuery: queryFromThread,
        queryName: queryFromThread,
        threadDatasetIds: resolvedDatasetIds,
        reportDatasetIds: resolvedDatasetIds.length > 0
          ? resolvedDatasetIds
          : prev.reportDatasetIds || [],
        ...(lastAiContent && { responsePreview: lastAiContent }),
      }));
    } catch (err) {
      console.error('Failed to load thread state:', err);
    } finally {
      setLoadingState(false);
    }
  };

  const handleDatasetsChange = (ids) => {
    const next = ids.map(String);
    // Functional update: a saved query still loading writes reportDatasetIds
    // from prev when it resolves, and a render-time spread here would let that
    // write bring back a dataset the user just removed.
    onSetFormData((prev) => ({
      ...prev,
      reportDatasetIds: next,
      threadDatasetIds: next,
    }));
  };

  // The conversational query control's tag row can only remove datasets.
  const handleRemoveDataset = (dsId) =>
    handleDatasetsChange(selectedDatasetIds.filter(x => x !== String(dsId)));

  const handleEditQuery = (e) => {
    const value = e.target.value;
    onSetFormData((prev) => ({
      ...prev,
      reportQuery: value,
      queryName: value,
      responsePreview: '',
      // Hand-editing the text divorces it from the saved query it came from, so
      // drop the binding — otherwise we'd persist report_query_thread_id pointing
      // at a query whose text no longer matches (PRD-1269 review).
      selectedThreadId: '',
      selectedSavedQueryTitle: '',
    }));
    setResponsePreview('');
  };

  const handleRegenerate = async () => {
    let query = (formData.reportQuery ?? formData.queryName ?? '').trim();
    if (!query) return;
    query = query.replace(/^\[Datasets?:.*\]\s*/i, '').replace(/^\[Files?: .+\]\s*/i, '').trim();
    if (!query) return;
    setLoadingState(true);
    setResponsePreview('');
    onSetFormData((prev) => ({ ...prev, responsePreview: '' }));
    try {
      // Read from ref to always get the latest selection
      const datasetIds = datasetIdsRef.current.length > 0
        ? [...datasetIdsRef.current]
        : undefined;
      const result = await apiService.runReportPreview({
        queryText: query, datasetIds,
      });
      const text = (result && result.response)
        ? String(result.response) : '';
      setResponsePreview(text);
      onSetFormData((prev) => ({ ...prev, responsePreview: text }));
    } catch (err) {
      console.error('Regenerate failed:', err);
      const detail = err?.detail;
      const errMsg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? (detail[0]?.msg || String(detail))
          : err?.message || 'Failed to run query';
      onSetFormData((prev) => ({
        ...prev, responsePreview: `Error: ${errMsg}`,
      }));
    } finally {
      setLoadingState(false);
    }
  };

  // PRD-1359: the selected rows as dataset RECORDS, for the review banner —
  // formData carries ids only, and the banner needs name + file_counts.
  const selectedDatasets = selectedDatasetIds
    .map((id) => datasets.find((d) => String(d.id) === id))
    .filter(Boolean);

  return (
    <div className="report-step-content">
      <h3 className="report-step-title">{title}</h3>

      <div className="report-form-field">
        <label className="report-label">{nameLabel}</label>
        <input
          type="text"
          className="resui-input"
          placeholder={namePlaceholder}
          value={formData.automationName ?? ''}
          onChange={(e) => onSetFormData({
            ...formData, automationName: e.target.value,
          })}
        />
      </div>

      {/* PRD-1359: above the Datasets FIELD — label included — with gap-4
          to the label (Figma "Report Step 2"; review feedback 2026-10-06). It
          used to sit between the label and the select. Repeated on step 4: an
          author who picks the dataset here may not reach step 4 for minutes. */}
      <ReviewRequiredBanner
        datasets={selectedDatasets}
        variant="automation"
        className="report-review-required-banner"
      />

      <div className="report-form-field">
        <label className="report-label">Datasets</label>
        <DatasetMultiSelect
          datasets={datasets}
          selectedIds={selectedDatasetIds}
          onChange={handleDatasetsChange}
          loading={loadingDatasets}
        />
        {showDatasetRequiredNote && selectedDatasetIds.length === 0 && !loadingDatasets && datasets.length > 0 && (
          <div className="report-preview-note report-preview-note--alert">
            <Icon name="warning_amber" className="report-preview-note-icon" size={18} />
            <span>A dataset is required. Select at least one dataset for the report to query against.</span>
          </div>
        )}
      </div>

      {showSavedQuery && (
      <div className="report-form-field">
        <label className="report-label">Select Saved Query</label>
        <div className="report-select-wrapper">
          <select
            className="resui-select report-select"
            value={selectedThreadId}
            onChange={handleSelectSavedQuery}
            disabled={loadingThreads}
          >
            <option value="">Select a saved query from ResAI</option>
            {threads.map((thread) => (
              <option key={thread.id} value={String(thread.id)}>
                {thread.title || 'Untitled conversation'}
              </option>
            ))}
          </select>
        </div>
        {loadingThreads && <span className="report-helper">Loading saved queries...</span>}
        {!loadingThreads && threads.length === 0 && (
          <span className="report-helper">No saved queries yet.</span>
        )}
        {/*
          Named rather than counted. "2 datasets could not be found" leaves the
          author guessing which, and a saved query referencing a renamed dataset
          is fixed by re-selecting that one — which they cannot do without
          knowing its name. role="alert" because this appears after an action
          they took and changes what the step will read.
        */}
        {unresolvedDatasets.length > 0 && (
          <span className="report-helper report-helper-warn" role="alert" data-testid="unresolved-datasets">
            {unresolvedDatasets.length === 1
              ? `This saved query refers to a dataset that is no longer available: ${unresolvedDatasets[0]}. Select it again below if it was renamed.`
              : `This saved query refers to datasets that are no longer available: ${unresolvedDatasets.join(', ')}. Select them again below if they were renamed.`}
          </span>
        )}
        {loadingState && selectedThreadId && (
          <span className="report-helper">Loading query text...</span>
        )}
      </div>
      )}

      {/*
        A third slot, alongside `runAction` and `preview` and for the same
        reason: conversational authoring (PRD-1381) replaces the query control
        while everything around it — the alert name, the dataset multi-select
        with its inline validation, the saved-query dropdown — stays exactly as
        it is. Replacing the whole step would clone those and let them drift,
        which is what this component exists to prevent.
      */}
      <div className="report-form-field">
        <label className="report-label">{queryLabel}</label>
        {(typeof queryControl === 'function'
          ? queryControl({ datasets, selectedDatasetIds, onRemoveDataset: handleRemoveDataset })
          : queryControl) || (
          <>
            <textarea
              className="resui-textarea"
              placeholder={queryPlaceholder}
              value={queryText}
              onChange={handleEditQuery}
              rows={4}
            />
            {queryHelper && <span className="report-helper">{queryHelper}</span>}
          </>
        )}
      </div>

      {showRunAndPreview && (runAction ? (
        <div className="report-run-action">
          <button
            type="button"
            className="report-button report-button-regenerate"
            onClick={runAction.onClick}
            disabled={runAction.disabled}
          >
            {runAction.label}
          </button>
          {runAction.footer}
        </div>
      ) : (
        <button
          type="button"
          className="report-button report-button-regenerate"
          onClick={handleRegenerate}
          disabled={
            loadingState
            || selectedDatasetIds.length === 0
            || !(formData.reportQuery ?? formData.queryName ?? '').trim()
          }
        >
          {loadingState ? 'Running…' : 'REGENERATE'}
        </button>
      ))}

      {showRunAndPreview && (
      <div className="report-form-field">
        <label className="report-label">{previewLabel}</label>
        <div className="report-response-preview">
          {preview ?? ((formData.responsePreview || responsePreview) ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {formData.responsePreview || responsePreview}
            </ReactMarkdown>
          ) : (
            'Run or select a query to see preview.'
          ))}
        </div>
        {!preview && (formData.responsePreview || responsePreview) && (
          <div className="report-preview-note">
            <Icon name="info_outline" className="report-preview-note-icon" size={18} />
            <span>This is a preview only. The report will be regenerated with the latest data each time it is delivered, so the final content may differ.</span>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ReportStepQuery;
